import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { STORAGE_KEYS } from '../../constants/storage';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, validateAuthState } = useAuth();
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const validationTimeoutRef = useRef<NodeJS.Timeout>();
  const lastValidationRef = useRef(0);
  const isValidRef = useRef(false);
  const isValidatingRef = useRef(true);

  isValidRef.current = isValid;
  isValidatingRef.current = isValidating;

  const validateAuth = useCallback(async () => {
    if (!isAuthenticated) {
      setIsValid(false);
      setIsValidating(false);
      return;
    }

    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    const savedUser = localStorage.getItem(STORAGE_KEYS.CURRENT_USER);
    if (!token || !savedUser) {
      setIsValid(false);
      setIsValidating(false);
      return;
    }

    if (isValidatingRef.current && isValidRef.current) {
      return;
    }

    const now = Date.now();
    if (now - lastValidationRef.current < 5000) {
      setIsValidating(false);
      return;
    }

    try {
      lastValidationRef.current = now;
      const validationResult = await validateAuthState();
      setIsValid(validationResult);
    } catch (error) {
      console.error('ProtectedRoute: Validation error:', error);
      setIsValid(false);
    } finally {
      setIsValidating(false);
    }
  }, [isAuthenticated, validateAuthState]);

  useEffect(() => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    validationTimeoutRef.current = setTimeout(() => {
      validateAuth();
    }, 100);

    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, [validateAuth]);

  // Keep the page mounted after the first successful check so child fetches
  // are not started over and over.
  if ((isLoading || isValidating) && !isValid) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If not authenticated or validation failed, redirect to login
  if (!isAuthenticated || !isValid) {
    console.log('ProtectedRoute: Access denied, redirecting to login', {
      isAuthenticated,
      isValid,
      isValidating,
      from: location.pathname
    });
    return <Navigate to="/login" state={{ redirectTo: location.pathname }} replace />;
  }

  // If authenticated and validation passed, render the protected content
  console.log('ProtectedRoute: Access granted, rendering content', {
    isAuthenticated,
    isValid,
    isValidating
  });
  return <>{children}</>;
};

export default React.memo(ProtectedRoute); 